/**
 * Currency ↔ cents conversion for the Membership Plans tab (DNKADM-0008 U4).
 *
 * Money on the wire is integer **cents** (`priceCents`); the UI displays and enters
 * **currency**. Conversion happens **only at the edge** (here) — the API wrapper and
 * the rest of the app move cents:
 *  - {@link formatCents} renders cents as a localized currency string for the table.
 *  - {@link parseCurrencyToCents} parses a typed currency entry back into integer
 *    cents for the write, validating it client-side (mirrors the server's integer
 *    cents) so a malformed price never hits the wire.
 *
 * USD is the only currency in scope (the design tokens / handoff are USD); the
 * formatter is centralized here so a later multi-currency need has one seam.
 */

/** The display/format currency (USD — the only currency in scope). */
const CURRENCY = 'USD';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: CURRENCY,
});

/**
 * Format integer cents as a localized currency string (e.g. `17000` → `"$170.00"`).
 * A non-finite input degrades to `$0.00` (defensive — the parsed plan already
 * defaults a missing `priceCents` to 0).
 */
export function formatCents(cents: number): string {
  const safe = Number.isFinite(cents) ? cents : 0;
  return currencyFormatter.format(safe / 100);
}

/**
 * Parse a typed currency entry (e.g. `"170"`, `"170.00"`, `"$1,700.50"`) into integer
 * cents. Accepts an optional leading currency symbol, thousands separators, and up to
 * two decimal places; rejects negatives, blanks, more than two decimals, and anything
 * non-numeric (so an out-of-shape price is blocked client-side before the write).
 *
 * Returns the parsed cents on success or a human message to show inline on the field.
 */
export function parseCurrencyToCents(
  raw: string,
): { ok: true; cents: number } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: false, message: 'Enter a price.' };
  }
  // Strip a leading currency symbol and thousands separators, then validate the
  // remaining number is a non-negative amount with at most two decimal places.
  const cleaned = trimmed.replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return { ok: false, message: 'Enter a valid price (e.g. 170 or 170.00).' };
  }
  const dollars = Number(cleaned);
  if (!Number.isFinite(dollars) || dollars < 0) {
    return { ok: false, message: 'Enter a valid price (e.g. 170 or 170.00).' };
  }
  // Round to the nearest cent to avoid binary-float drift (e.g. 170.07 * 100).
  const cents = Math.round(dollars * 100);
  return { ok: true, cents };
}

/**
 * Render integer cents as a plain editable currency value (no symbol/grouping) for
 * seeding the price input on edit — e.g. `17000` → `"170.00"`, `0` → `"0.00"`.
 */
export function centsToInputValue(cents: number): string {
  const safe = Number.isFinite(cents) ? cents : 0;
  return (safe / 100).toFixed(2);
}
